import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useNotificationStats } from '@/hooks/use-notifications';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: notifStats } = useNotificationStats();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(INACTIVITY_TIMEOUT_MS);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const raw = localStorage.getItem('xs_admin_session');
        if (!raw) return;
        const key = 'xs-admin-portal-key';
        const decoded = atob(raw);
        const json = decodeURIComponent(
          decoded
            .split('')
            .map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length)))
            .join(''),
        );
        const session = JSON.parse(json) as { lastActivity: number };
        const elapsed = Date.now() - session.lastActivity;
        setRemainingMs(Math.max(0, INACTIVITY_TIMEOUT_MS - elapsed));
      } catch {
        // Ignore decode errors
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = useCallback(async () => {
    setDropdownOpen(false);
    await logout();
  }, [logout]);

  const displayName = user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const timeoutWarning = minutes < 5;

  return (
    <header className="h-16 border-b border-slate-200 flex items-center justify-between px-8 bg-white sticky top-0 z-10">
      {/* Search */}
      <div className="max-w-md w-full">
        <div className="relative flex items-center">
          <span className="material-symbols-outlined absolute left-3 text-slate-400">search</span>
          <input
            className="w-full bg-slate-100 border-none rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-primary/20 placeholder:text-slate-500"
            placeholder="Search customers, invoices, transactions..."
            aria-label="Search customers, invoices, transactions"
            type="text"
          />
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-4">
        {/* Session timeout */}
        <div
          className={`flex items-center gap-1.5 text-xs ${timeoutWarning ? 'text-red-500' : 'text-slate-400'}`}
          title="Session timeout remaining"
        >
          <span className="material-symbols-outlined text-base">timer</span>
          <span className="tabular-nums">{minutes}:{seconds.toString().padStart(2, '0')}</span>
        </div>

        {/* Notifications */}
        <button
          type="button"
          onClick={() => navigate('/alerts')}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 relative"
          title="View alerts"
        >
          <span className="material-symbols-outlined">notifications</span>
          {(notifStats?.unread ?? 0) > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-[10px] font-bold text-white px-1">
              {notifStats!.unread > 99 ? '99+' : notifStats!.unread}
            </span>
          )}
        </button>

        <div className="h-8 w-px bg-slate-200 mx-2"></div>

        {/* Profile */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors"
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
          >
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-slate-900">Corporate HQ</p>
              <p className="text-[10px] text-slate-500">Johannesburg, ZA</p>
            </div>
            <div className="size-9 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">
              {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
            </div>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
              <div className="px-4 py-2 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-900">{displayName}</p>
                <p className="text-xs text-slate-500">{user?.role || 'Admin'}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-base">logout</span>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
